import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Avatar from "@/components/chat/Avatar";
import { X, Users, User as UserIcon, Check, Heart, Clock, Sparkles } from "lucide-react";

const EMPTY_DIR = { friends: [], recent: [], newcomers: [] };

export default function NewChatDialog({ open, onClose, onCreated }) {
  const [directory, setDirectory] = useState(EMPTY_DIR);
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setIsGroup(false);
    setGroupName("");
    setError("");
    setQuery("");
    setSearchResults(null);
    (async () => {
      try {
        const { data } = await api.get("/users/me/directory");
        setDirectory({
          friends: data?.friends || [],
          recent: data?.recent || [],
          newcomers: data?.newcomers || [],
        });
      } catch (e) {
        setError(formatApiError(e));
        setDirectory(EMPTY_DIR);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/search", { params: { q } });
        setSearchResults(data || []);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const toggleUser = (u) => {
    setSelected((prev) => {
      const exists = prev.find((x) => x.id === u.id);
      if (exists) return prev.filter((x) => x.id !== u.id);
      if (!isGroup) return [u];
      return [...prev, u];
    });
  };

  const submit = async () => {
    setError("");
    if (selected.length === 0) return setError("En az bir kişi seç");
    if (isGroup && !groupName.trim()) return setError("Grup için bir isim gerekli");
    setSubmitting(true);
    try {
      const { data } = await api.post("/conversations", {
        participant_ids: selected.map((u) => u.id),
        is_group: isGroup,
        name: isGroup ? groupName.trim() : null,
      });
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isSearching = searchResults !== null;
  const totalDirectoryUsers =
    directory.friends.length + directory.recent.length + directory.newcomers.length;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
      data-testid="new-chat-dialog"
    >
      <div className="bg-white border-hard shadow-hard-lg rounded-2xl w-full max-w-lg p-6 slide-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-3xl">Yeni sohbet</h2>
          <button
            onClick={onClose}
            data-testid="close-new-chat-btn"
            aria-label="Kapat"
            className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn"
          >
            <X strokeWidth={3} className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            data-testid="switch-direct-btn"
            onClick={() => {
              setIsGroup(false);
              setSelected((s) => s.slice(0, 1));
            }}
            className={`flex-1 border-hard rounded-xl px-4 py-2 font-black uppercase text-xs tracking-widest press-btn ${!isGroup ? "bg-[#CCFF00] shadow-hard-sm" : "bg-white"}`}
          >
            <div className="flex items-center justify-center gap-2">
              <UserIcon strokeWidth={3} className="w-4 h-4" /> Birebir
            </div>
          </button>
          <button
            data-testid="switch-group-btn"
            onClick={() => setIsGroup(true)}
            className={`flex-1 border-hard rounded-xl px-4 py-2 font-black uppercase text-xs tracking-widest press-btn ${isGroup ? "bg-[#D4B3FF] shadow-hard-sm" : "bg-white"}`}
          >
            <div className="flex items-center justify-center gap-2">
              <Users strokeWidth={3} className="w-4 h-4" /> Grup
            </div>
          </button>
        </div>

        {isGroup && (
          <input
            data-testid="group-name-input"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Grup adı (ör. Cuma akşamı)"
            className="w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-2.5 mb-3 focus:outline-none focus:bg-white"
          />
        )}

        <input
          data-testid="new-chat-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="İsim veya e-posta ara…"
          className="w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-2.5 mb-3 focus:outline-none focus:bg-white"
        />

        <div className="max-h-[22rem] overflow-auto border-hard-2 rounded-xl">
          {isSearching ? (
            <FlatUserList
              testid="new-chat-search-list"
              users={searchResults}
              selected={selected}
              toggleUser={toggleUser}
              emptyText="Bu aramaya uyan kişi yok"
            />
          ) : totalDirectoryUsers === 0 ? (
            <div className="p-6 text-center text-sm font-semibold text-[#555]">
              Henüz gösterilecek kişi yok. E-posta veya isim ile ara.
            </div>
          ) : (
            <div className="divide-y-2 divide-[#0A0A0A]">
              <UserSection
                testid="section-friends"
                icon={Heart}
                tint="#FFC6FF"
                title="Arkadaşlarım"
                users={directory.friends}
                selected={selected}
                toggleUser={toggleUser}
              />
              <UserSection
                testid="section-recent"
                icon={Clock}
                tint="#CCFF00"
                title="Yakın zamanda konuştuklarım"
                users={directory.recent}
                selected={selected}
                toggleUser={toggleUser}
              />
              <UserSection
                testid="section-newcomers"
                icon={Sparkles}
                tint="#A8E6FF"
                title="Yeni katılanlar"
                users={directory.newcomers}
                selected={selected}
                toggleUser={toggleUser}
              />
            </div>
          )}
        </div>

        {error && (
          <div
            className="mt-3 text-sm font-bold bg-[#FF9E80] border-hard-2 rounded-lg px-3 py-2"
            data-testid="new-chat-error"
          >
            {error}
          </div>
        )}

        <button
          data-testid="create-conversation-btn"
          onClick={submit}
          disabled={submitting}
          className="mt-4 w-full bg-[#CCFF00] border-hard shadow-hard rounded-xl px-5 py-3 font-black press-btn disabled:opacity-60"
        >
          {submitting ? "Oluşturuluyor…" : "Sohbeti başlat"}
        </button>
      </div>
    </div>
  );
}

function UserSection({ testid, icon: Icon, tint, title, users, selected, toggleUser }) {
  if (!users || users.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div
        className="sticky top-0 z-10 flex items-center gap-2 px-3 py-1.5 border-b-2 border-[#0A0A0A]"
        style={{ background: tint }}
      >
        <Icon strokeWidth={3} className="w-3.5 h-3.5" />
        <div className="text-[10px] font-black uppercase tracking-widest">{title}</div>
        <div className="ml-auto text-[10px] font-black opacity-70">{users.length}</div>
      </div>
      {users.map((u) => (
        <UserRow key={u.id} user={u} selected={selected} toggleUser={toggleUser} />
      ))}
    </div>
  );
}

function FlatUserList({ testid, users, selected, toggleUser, emptyText }) {
  if (!users || users.length === 0) {
    return (
      <div className="p-6 text-center text-sm font-semibold text-[#555]" data-testid={`${testid}-empty`}>
        {emptyText}
      </div>
    );
  }
  return (
    <div className="divide-y-2 divide-[#0A0A0A]" data-testid={testid}>
      {users.map((u) => (
        <UserRow key={u.id} user={u} selected={selected} toggleUser={toggleUser} />
      ))}
    </div>
  );
}

function UserRow({ user: u, selected, toggleUser }) {
  const isSelected = !!selected.find((x) => x.id === u.id);
  return (
    <button
      key={u.id}
      onClick={() => toggleUser(u)}
      data-testid={`select-user-${u.id}`}
      className={`w-full flex items-center gap-3 p-3 hover:bg-[#F9F8F6] text-left ${isSelected ? "bg-[#CCFF00]/40" : ""}`}
    >
      <Avatar name={u.name} picture={u.picture} size={38} online={u.online} showStatus />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{u.name}</div>
        <div className="text-xs text-[#555] truncate">{u.email}</div>
      </div>
      {isSelected && <Check strokeWidth={3} className="w-5 h-5 shrink-0" />}
    </button>
  );
}
